import { useLanguage } from "@/contexts/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ProductEntry } from "@/types/product/productStep.types";
import { ExtractionProductForm, TransformationProductForm, MixedActivityProductForm } from "../../forms/product-forms";

/**
 * ProductEntryModal
 * Modal dialog for adding/editing a product entry.
 * Orchestrates the correct form (extraction, transformation, mixed) based on activityType.
 * All validation and form state are handled in the respective form components/hooks.
 */
export interface ProductEntryModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (entry: ProductEntry) => void;
  formEntry: ProductEntry;
  editing: boolean;
  generalStoneTypeId?: string;
  activityType?: string;
  calculationInputs: any;
  allEntries: ProductEntry[];
  totalAnnualProduction: number;
  totalRevenue: number;
  formData?: any;
  extractionProducts: ProductEntry[];
  currentCompanyId?: string;
}

export const ProductEntryModal = ({
  open,
  onClose,
  onSave,
  formEntry,
  editing,
  generalStoneTypeId,
  activityType,
  calculationInputs,
  allEntries,
  totalAnnualProduction,
  totalRevenue,
  formData,
  extractionProducts,
  currentCompanyId,
}: ProductEntryModalProps) => {
  const { t } = useLanguage();

  // Dynamically render the appropriate form component based on activity type
  const renderFormContent = () => {
    switch (activityType) {
      case "extraction":
        return (
          <ExtractionProductForm
            formEntry={formEntry}
            onSave={onSave}
            onClose={onClose}
            editing={editing}
            generalStoneTypeId={generalStoneTypeId}
            calculationInputs={calculationInputs}
            allEntries={allEntries}
            totalAnnualProduction={totalAnnualProduction}
            totalRevenue={totalRevenue}
            formData={formData}
          />
        );
      case "transformation":
        return (
          <TransformationProductForm
            formEntry={formEntry}
            onSave={onSave}
            onClose={onClose}
            editing={editing}
            calculationInputs={calculationInputs}
            allEntries={allEntries}
            totalAnnualProduction={totalAnnualProduction}
            totalRevenue={totalRevenue}
            formData={formData}
            extractionProducts={extractionProducts}
            currentCompanyId={currentCompanyId}
          />
        );
      case "extraction_and_transformation":
        return (
          <MixedActivityProductForm
            formEntry={formEntry}
            onSave={onSave}
            onClose={onClose}
            editing={editing}
            generalStoneTypeId={generalStoneTypeId}
            calculationInputs={calculationInputs}
            allEntries={allEntries}
            totalAnnualProduction={totalAnnualProduction}
            totalRevenue={totalRevenue}
            extractionProducts={extractionProducts}
            currentCompanyId={currentCompanyId}
          />
        );
      default:
        return (
          <div className="text-center py-8">
            <p className="text-red-500">{t("errors.unsupportedActivityType")}</p>
          </div>
        );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl text-teal-700">
            {editing ? t("products.editProduct") : t("products.addProduct")}
          </DialogTitle>
        </DialogHeader>
        {renderFormContent()}
      </DialogContent>
    </Dialog>
  );
};
